import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Classes from './Navbar.module.css';
import Logo from '../Logo/Logo';
import useScreenWidth from '../../Hooks/useScreenWidth';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        let rafId = null;

        const handleScroll = () => {
            if (rafId !== null) return;
            rafId = window.requestAnimationFrame(() => {
                const nextValue = window.scrollY > 22;
                setScrolled((prev) => (prev === nextValue ? prev : nextValue));
                rafId = null;
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const location = useLocation();
    const [activePage, setActivePage] = useState(location.pathname);

    useEffect(() => {
        setActivePage(location.pathname);
    }, [location.pathname]);

    const screenWidth = useScreenWidth();
    const isMobile = screenWidth < 640;
    const isMenuLayout = screenWidth <= 900;
    const isHomeActive = activePage === '/';
    const isServicesActive = activePage === '/services' || activePage.startsWith('/services/');
    const isCompanyActive = activePage === '/company';
    const isPortfolioActive = activePage === '/portfolio' || activePage.startsWith('/portfolio/');
    const isBlogActive = activePage === '/blog' || activePage.startsWith('/blog/');
    const isCertificatesActive = activePage === '/certificates' || activePage.startsWith('/certificates/');
    const isContactActive = activePage === '/contact-us';

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        if (!isMenuLayout && mobileMenuOpen) {
            setMobileMenuOpen(false);
        }
    }, [isMenuLayout, mobileMenuOpen]);

    useEffect(() => {
        if (!mobileMenuOpen) return;

        const onEscape = (event) => {
            if (event.key === 'Escape') {
                setMobileMenuOpen(false);
            }
        };

        window.addEventListener('keydown', onEscape);
        return () => window.removeEventListener('keydown', onEscape);
    }, [mobileMenuOpen]);

    return (
        <>
            <div id='site-navbar' className={`${Classes.navbar} ${scrolled ? Classes.navbarScrolled : ''}`}>
                <div className={Classes.leftCluster}>
                    <Link to='/'>
                        <div className={Classes.logoWrap}>
                            <Logo />
                        </div>
                    </Link>

                    {!isMobile ? (
                        <span className={Classes.verifiedBadge}>
                            <VerifiedRoundedIcon />
                            Govt Approved
                        </span>
                    ) : null}
                </div>

                {!isMenuLayout ? (
                    <ul className={Classes.navList}>
                        <li
                            className={isHomeActive ? Classes.navActive : ''}
                        >
                            <Link to='/' aria-label='Home'>
                                {isMobile ? <HomeRoundedIcon /> : 'Home'}
                            </Link>
                        </li>
                        <li
                            className={isServicesActive ? Classes.navActive : ''}
                        >
                            <Link to='/services'>Services</Link>
                        </li>
                        <li
                            className={isCompanyActive ? Classes.navActive : ''}
                        >
                            <Link to='/company'>Company</Link>
                        </li>
                        <li
                            className={isPortfolioActive ? Classes.navActive : ''}
                        >
                            <Link to='/portfolio'>Portfolio</Link>
                        </li>
                        <li
                            className={isBlogActive ? Classes.navActive : ''}
                        >
                            <Link to='/blog'>Blog</Link>
                        </li>
                        <li
                            className={isCertificatesActive ? Classes.navActive : ''}
                        >
                            <Link to='/certificates'>Certificates</Link>
                        </li>
                        <li
                            className={isContactActive ? Classes.navActive : ''}
                        >
                            <Link to='/contact-us'>Contact Us</Link>
                        </li>
                    </ul>
                ) : (
                    <>
                        <div className={Classes.mobileTitle} aria-label='DevZona mobile title'>
                            <span className={Classes.titleBrand}>
                                Dev<span>ZONA</span>
                            </span>
                            <span className={Classes.titleDivider}>•</span>
                            <span className={Classes.titleAi}>
                                Agentic <span className={Classes.titleAiHighlight}>AI</span> Labs
                                <BoltRoundedIcon className={Classes.titleBolt} />
                            </span>
                        </div>
                        <button
                            type='button'
                            className={Classes.menuToggle}
                            onClick={() => setMobileMenuOpen((prev) => !prev)}
                            aria-expanded={mobileMenuOpen}
                            aria-controls='mobile-site-menu'
                            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                        >
                            {mobileMenuOpen ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
                        </button>
                    </>
                )}

                <div className={Classes.gridRight}>
                    <a
                        href='https://wa.me/923290992222'
                        target='_blank'
                        rel='noreferrer'
                    >
                        <button className={Classes.quoteBtn}>
                            {isMobile ? 'Consult' : 'Book Consultation'}
                            <RocketLaunchRoundedIcon />
                        </button>
                    </a>
                </div>
            </div>

            {isMenuLayout && mobileMenuOpen ? (
                <div className={Classes.mobileMenuLayer}>
                    <button
                        type='button'
                        className={Classes.mobileMenuBackdrop}
                        aria-label='Close menu overlay'
                        onClick={() => setMobileMenuOpen(false)}
                    />

                    <div id='mobile-site-menu' className={Classes.mobileMenuPanel}>
                        <ul className={Classes.mobileNavList}>
                            <li className={isHomeActive ? Classes.navActive : ''}>
                                <Link to='/'>
                                    Home
                                </Link>
                            </li>
                            <li className={isServicesActive ? Classes.navActive : ''}>
                                <Link to='/services'>Services</Link>
                            </li>
                            <li className={isCompanyActive ? Classes.navActive : ''}>
                                <Link to='/company'>Company</Link>
                            </li>
                            <li className={isPortfolioActive ? Classes.navActive : ''}>
                                <Link to='/portfolio'>Portfolio</Link>
                            </li>
                            <li className={isBlogActive ? Classes.navActive : ''}>
                                <Link to='/blog'>Blog</Link>
                            </li>
                            <li className={isCertificatesActive ? Classes.navActive : ''}>
                                <Link to='/certificates'>Certificates</Link>
                            </li>
                            <li className={isContactActive ? Classes.navActive : ''}>
                                <Link to='/contact-us'>Contact Us</Link>
                            </li>
                        </ul>

                        <a
                            href='https://wa.me/923290992222'
                            target='_blank'
                            rel='noreferrer'
                            className={Classes.mobileMenuCta}
                        >
                            Book Consultation
                            <RocketLaunchRoundedIcon />
                        </a>

                        <a
                            href='https://wa.me/923290992222'
                            target='_blank'
                            rel='noreferrer'
                            className={Classes.mobileMenuWhatsapp}
                        >
                            <WhatsAppIcon />
                            WhatsApp Support
                        </a>
                    </div>
                </div>
            ) : null}
        </>
    );
}
